import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { PageToolbar, usePageTranslation } from "@/components/PageToolbar";

const INTRO = { title: "People of Cinema", sub: "Directors, actors, animators and pioneers — verified career profiles from every film culture." };

export default function People() {
  const [people, setPeople] = useState([]);
  const [q, setQ] = useState("");
  const [hofOnly, setHofOnly] = useState(false);

  useEffect(() => {
    const params = {};
    if (q) params.q = q;
    if (hofOnly) params.hall_of_fame = true;
    api.get("/people", { params }).then((r) => setPeople(r.data));
  }, [q, hofOnly]);

  const blocks = useMemo(() => INTRO, []);
  const { lang, tr, busy } = usePageTranslation(blocks, "people-intro");

  return (
    <main data-testid="people-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <p className="eyebrow">Careers & Creative Crafts</p>
      <h1 className="font-display font-bold text-4xl sm:text-5xl mt-2 text-[#1c1512]">{tr("title", INTRO.title)}</h1>
      <p className="text-[#6b5d51] mt-3 max-w-2xl">{tr("sub", INTRO.sub)}</p>
      <div className="mt-5">
        <PageToolbar speakText={`${tr("title", INTRO.title)}. ${tr("sub", INTRO.sub)}`} busy={busy} lang={lang} />
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-[#fbf8f1] border border-[#d8ccb8] rounded-full px-4 py-2">
          <Search className="w-4 h-4 text-[#6b5d51]" />
          <input data-testid="people-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people…" className="bg-transparent text-sm outline-none w-44" />
        </div>
        <button
          data-testid="hall-of-fame-toggle"
          onClick={() => setHofOnly(!hofOnly)}
          className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border transition-colors ${hofOnly ? "bg-[#7d1a1a] text-[#f6f1e7] border-[#7d1a1a]" : "bg-[#fbf8f1] border-[#d8ccb8] hover:border-[#7d1a1a]"}`}
        >
          <Trophy className="w-4 h-4" /> Hall of Fame
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        {people.map((p) => {
          const [c1, c2] = ["#171210", "#7d1a1a"];
          return (
            <Link key={p.id} to={`/person/${p.id}`} data-testid={`person-card-${p.id}`} className="poster-card bg-[#fbf8f1] border border-[#d8ccb8] group">
              <div className="h-24 flex items-end p-4" style={{ background: `linear-gradient(135deg, ${c1}, ${c2})` }}>
                <span className="font-display font-bold text-4xl text-[#f6f1e7]/90">{p.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>
              </div>
              <div className="p-5">
                <h2 className="font-display font-bold text-xl group-hover:text-[#7d1a1a] transition-colors">{p.name}</h2>
                <p className="text-sm text-[#b06810] mt-1">{p.roles.join(" · ")}</p>
                <p className="text-sm text-[#6b5d51] mt-1">{p.country} · b. {p.born}{p.died ? ` – ${p.died}` : ""}</p>
                {p.hall_of_fame && <p className="font-mono-label text-[9px] tracking-[0.2em] uppercase text-[#7d1a1a] mt-2">★ Hall of Fame</p>}
              </div>
            </Link>
          );
        })}
      </div>
      {people.length === 0 && <p data-testid="people-empty-state" className="text-center py-16 font-display text-xl text-[#6b5d51]">No profiles found.</p>}
    </main>
  );
}
